import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import * as LibraryAttachments from "../../services/library-attachments.ts";
import * as Logger from "../../services/logger.ts";
import { type LibraryNote } from "../../../models/library-note.ts";
import { type AppStore } from "../app-store.ts";
import * as Config from "../../client-config.ts";

// Components
import "../app/app-toolbar.ts";
import "../dui/markdown/dui-markdown.ts";
import "../app/view-header.ts";
import "../app/loading.ts";
import "../dui/page-inset/dui-page-inset.ts";
import "../dui/scroll-area/dui-scroll-area.ts";
import "../dui/vstack/dui-vstack.ts";
import "../dui/hstack/dui-hstack.ts";
import "../dui/button/dui-button.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/badge/dui-badge.ts";
import "../dui/breadcrumb/dui-breadcrumb.ts";
import "../dui/breadcrumb/dui-breadcrumb-list.ts";
import "../dui/breadcrumb/dui-breadcrumb-item.ts";
import "../dui/breadcrumb/dui-breadcrumb-link.ts";
import "../dui/breadcrumb/dui-breadcrumb-separator.ts";
import "../dui/breadcrumb/dui-breadcrumb-page.ts";
import "../dui/trunc/dui-trunc.ts";
import "../dui/link/dui-link.ts";

const logger = Logger.create({ source: "client/library-note-view" });

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const handleDownloadAttachment = async (
  sha256: string,
  filename: string,
  accessToken: string | undefined,
): Promise<void> => {
  if (!accessToken) {
    logger.error("Cannot download attachment: no access token");
    return;
  }

  const result = await LibraryAttachments.downloadLibraryAttachment(
    accessToken,
    sha256,
    filename,
  );

  if (!result.ok) {
    logger.error("Failed to download attachment", { error: result.error });
  }
};

const formatConfidence = (confidence: number): string =>
  `${Math.round(confidence * 100)}%`;

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const styles = css`
  :host {
    display: flex;
    flex-direction: column;
    height: 100cqh;
    width: 100cqw;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .attachment-chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .filename {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
  }

  .filesize {
    color: var(--muted-foreground);
    font-size: var(--font-size-sm);
  }

  .metadata {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    color: var(--muted-foreground);
    font-size: var(--font-size-sm);
  }

  .metadata-label {
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
  }

  .source-link {
    color: var(--muted-foreground);
    text-decoration: underline;
    text-decoration-color: color-mix(
      in oklch,
      var(--muted-foreground) 50%,
      transparent
    );
  }

  .source-link:hover {
    color: var(--foreground);
  }

  .section-label {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
  }
`;

const tagsView = (tags: string[]): TemplateResult | typeof nothing => {
  if (tags.length === 0) return nothing;
  return html`
    <div class="tags">
      ${tags.map(
        (tag) => html`<dui-badge variant="secondary">${tag}</dui-badge>`,
      )}
    </div>
  `;
};

const attachmentsView = (
  _note: LibraryNote,
  accessToken: string | undefined,
  attachments: Array<{
    sha256: string;
    meta: { filename: string; size_bytes: number };
  }>,
): TemplateResult | typeof nothing => {
  if (attachments.length === 0) return nothing;
  return html`
    <div>
      <div class="section-label">Attachments</div>
      <div class="attachment-chips" style="margin-top: var(--space-2)">
        ${attachments.map((attachment) => {
          const onDownload = (e: Event) => {
            e.stopPropagation();
            handleDownloadAttachment(
              attachment.sha256,
              attachment.meta.filename,
              accessToken,
            );
          };
          return html`
            <dui-button variant="secondary" size="sm" @click="${onDownload}">
              <dui-hstack gap="2">
                <dui-icon icon="attachment"></dui-icon>
                <span class="filename">${attachment.meta.filename}</span>
                <span class="filesize"
                  >${formatFileSize(attachment.meta.size_bytes)}</span
                >
              </dui-hstack>
            </dui-button>
          `;
        })}
      </div>
    </div>
  `;
};

const sourceUrlsView = (urls: string[]): TemplateResult | typeof nothing => {
  if (urls.length === 0) return nothing;
  return html`
    <div>
      <span class="metadata-label">Sources:</span>
      ${urls.map(
        (url) =>
          html` <a class="source-link" href="${url}" target="_blank"
            >${url}</a
          >`,
      )}
    </div>
  `;
};

const metadataView = (note: LibraryNote): TemplateResult => {
  return html`
    <div class="metadata">
      <div>
        <span class="metadata-label">Confidence:</span>
        ${formatConfidence(note.confidence)}
      </div>
      ${sourceUrlsView(note.source_urls)}
      <div>
        <span class="metadata-label">Created:</span>
        ${formatDate(note.created_at)}
      </div>
    </div>
  `;
};

/**
 * Library note detail view component.
 */
@customElement("library-note-view")
export class LibraryNoteViewElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.base, styles];

  @property({ attribute: false })
  accessor store: AppStore | undefined = undefined;

  override render(): TemplateResult {
    if (!this.store) {
      return html``;
    }

    const state = this.store.get();
    const accessToken = state.session?.access_token;

    // Loading state
    if (state.libraryNoteLoading) {
      return html`
        <app-toolbar>
          <dui-breadcrumb>
            <dui-breadcrumb-list>
              <dui-breadcrumb-item>
                <dui-breadcrumb-link>
                  <dui-link href="${Config.LIBRARY_URL}">Library</dui-link>
                </dui-breadcrumb-link>
              </dui-breadcrumb-item>
              <dui-breadcrumb-separator></dui-breadcrumb-separator>
              <dui-breadcrumb-item>
                <dui-breadcrumb-page>Loading...</dui-breadcrumb-page>
              </dui-breadcrumb-item>
            </dui-breadcrumb-list>
          </dui-breadcrumb>
        </app-toolbar>
        <loading-view></loading-view>
      `;
    }

    // Error state
    if (state.libraryNoteError) {
      return html`
        <app-toolbar>
          <dui-breadcrumb>
            <dui-breadcrumb-list>
              <dui-breadcrumb-item>
                <dui-breadcrumb-link>
                  <dui-link href="${Config.LIBRARY_URL}">Library</dui-link>
                </dui-breadcrumb-link>
              </dui-breadcrumb-item>
            </dui-breadcrumb-list>
          </dui-breadcrumb>
        </app-toolbar>
        <dui-page-inset>
          <p>Error loading note: ${state.libraryNoteError}</p>
        </dui-page-inset>
      `;
    }

    const note = state.libraryNote;

    // Not found state
    if (!note) {
      return html`
        <app-toolbar>
          <dui-breadcrumb>
            <dui-breadcrumb-list>
              <dui-breadcrumb-item>
                <dui-breadcrumb-link>
                  <dui-link href="${Config.LIBRARY_URL}">Library</dui-link>
                </dui-breadcrumb-link>
              </dui-breadcrumb-item>
            </dui-breadcrumb-list>
          </dui-breadcrumb>
        </app-toolbar>
        <dui-page-inset>
          <p>Note not found.</p>
        </dui-page-inset>
      `;
    }

    // Get attachment data from the library items cache
    const slug = state.slug;
    const noteItem = slug ? state.libraryNoteItems.get(slug) : undefined;
    const attachments = noteItem?.attachments ?? [];

    return html`
      <app-toolbar>
        <dui-breadcrumb>
          <dui-breadcrumb-list>
            <dui-breadcrumb-item>
              <dui-breadcrumb-link>
                <dui-link href="${Config.LIBRARY_URL}">Library</dui-link>
              </dui-breadcrumb-link>
            </dui-breadcrumb-item>
            <dui-breadcrumb-separator></dui-breadcrumb-separator>
            <dui-breadcrumb-item>
              <dui-breadcrumb-page>
                <dui-trunc>${note.title}</dui-trunc>
              </dui-breadcrumb-page>
            </dui-breadcrumb-item>
          </dui-breadcrumb-list>
        </dui-breadcrumb>
      </app-toolbar>
      <dui-scroll-area fade>
        <dui-page-inset max-width="36rem">
          <dui-vstack gap="6">
            <view-header>${note.title}</view-header>
            ${metadataView(note)} ${tagsView(note.tags)}
            <dui-markdown .value="${note.content}"></dui-markdown>
            ${attachmentsView(note, accessToken, attachments)}
          </dui-vstack>
        </dui-page-inset>
      </dui-scroll-area>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "library-note-view": LibraryNoteViewElement;
  }
}
